import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import type { BaseProps, ForwardProps, NoChildrenProp } from '../../types';
import { useDirection, useI18n } from '../../hooks';
import Icon, { registerIcon } from '../Icon';
import * as caretLeftIcon from '../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import Button from '../Button';
import Flex from '../Flex';

registerIcon(caretLeftIcon, caretRightIcon);

export interface PaginationProps extends BaseProps, NoChildrenProp {
  /** Total number of items. */
  total: number;
  /**
   * Size of each page.
   * @default 10
   */
  pageSize?: number;
  /**
   * Current page number.
   * @default 1
   */
  pageNumber?: number;
  /** Function to handle page change. */
  onPageChange?: (pageNumber: number) => void;
}

const defaultProps: Partial<PaginationProps> = {
  pageNumber: 1,
  pageSize: 10
};

const Pagination: FunctionComponent<PaginationProps & ForwardProps> = (props: PaginationProps) => {
  const { total, onPageChange, ...restProps } = props;
  let { pageSize = 10, pageNumber = 1 } = props;

  const t = useI18n();

  pageSize = Math.max(1, pageSize);
  const pageCount = Math.ceil(total / pageSize);
  if (pageNumber < 1) pageNumber = 1;
  if (pageNumber > pageCount) pageNumber = pageCount;

  const handlePageChange = useCallback(
    (currentValue: number) => {
      if (onPageChange) onPageChange(currentValue);
    },
    [onPageChange]
  );

  const { start, end } = useDirection();

  return (
    <Flex
      container={{
        gap: 1
      }}
      aria-label={t('pagination_page_of', [pageNumber, pageCount])}
      role='tablist'
      total={total}
      {...restProps}
    >
      <Flex container={{ alignItems: 'center' }} aria-hidden>
        {t('pagination_page_of', [pageNumber, pageCount])}
      </Flex>
      <Flex
        container={{
          gap: 1
        }}
      >
        <Button
          variant='simple'
          label={t('pagination_prev')}
          disabled={pageNumber === 1}
          onClick={() => handlePageChange(pageNumber - 1)}
          icon
        >
          <Icon name={`caret-${start}`} />
        </Button>
        <Button
          variant='simple'
          label={t('pagination_next')}
          disabled={pageNumber === pageCount}
          onClick={() => handlePageChange(pageNumber + 1)}
          icon
        >
          <Icon name={`caret-${end}`} />
        </Button>
      </Flex>
    </Flex>
  );
};

Pagination.defaultProps = defaultProps;

export default Pagination;
